import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ActiveWorkRun } from '@sr-test-wizard/shared';
import { useAuth } from '../auth/AuthContext';
import { StatusIcon, type IconKind } from './StatusIcon';
import { useActiveWorkStore } from '../state/activeWorkStore';
import { useSessionRunsStore, type PinnedRun } from '../state/sessionRunsStore';

function flagLabels(run: ActiveWorkRun | null | undefined): string[] {
  if (!run) return [];
  const labels: string[] = [];
  if (run.flags.autoWorkflowBusy) labels.push('Auto');
  if (run.flags.texecAnalyzing) labels.push('TEXEC');
  if (run.flags.videoBusy) labels.push('Video');
  if (run.flags.imagesBusy) labels.push('Images');
  if (run.flags.pullSourceBusy) labels.push('Source');
  if (run.flags.driveVideoBusy) labels.push('Drive');
  return labels.length ? labels : run.jobs.map((j) => j.type);
}

function isBusy(run: ActiveWorkRun | null | undefined): boolean {
  if (!run) return false;
  const f = run.flags;
  return (
    !!f.autoWorkflowBusy ||
    f.texecAnalyzing ||
    f.videoBusy ||
    f.imagesBusy ||
    f.pullSourceBusy ||
    f.driveVideoBusy ||
    run.jobs.some((j) => j.status === 'queued' || j.status === 'running')
  );
}

function jobSummary(run: ActiveWorkRun | null | undefined, pinned: PinnedRun): string {
  if (!run) return `${pinned.gameName} · ${pinned.texecKey}\nIdle — pinned until you close`;
  const lines = run.jobs.map((j) => `${j.type}: ${j.status}`);
  if (run.flags.autoWorkflowBusy) {
    lines.push(
      `Auto workflow: ${run.autoWorkflowMessage || run.autoWorkflowStep || 'running'}`,
    );
  }
  if (run.flags.texecAnalyzing) lines.push('TEXEC analyze-all: running');
  if (run.flags.videoBusy && !run.jobs.some((j) => j.type === 'analyze-videos')) {
    lines.push('Video extract/analyze: running');
  }
  if (run.workerName) lines.push(`Worker: ${run.workerName}`);
  return lines.join('\n') || 'Active work';
}

function ActiveWorkChip({
  pinned,
  live,
  active,
  onOpen,
  onClose,
}: {
  pinned: PinnedRun;
  live: ActiveWorkRun | null;
  active: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);
  const busy = isBusy(live);
  const labels = busy ? flagLabels(live) : [];
  const queued = live?.jobs.filter((j) => j.status === 'queued').length ?? 0;
  const iconStatus: IconKind = busy ? 'in_progress' : 'paused';

  return (
    <div className={`active-work-chip-wrap${active ? ' is-current' : ''}`}>
      <button
        type="button"
        className={`active-work-chip${active ? ' active' : ''}${busy ? ' is-busy' : ' is-idle'}`}
        title={jobSummary(live, pinned)}
        onClick={onOpen}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <StatusIcon status={iconStatus} size={14} />
        <span className="active-work-sr">{pinned.srKey || live?.srKey || 'SR'}</span>
        {live?.initials && (
          <span className="active-work-initials" title={live.workerName || live.workerEmail || ''}>
            {live.initials}
          </span>
        )}
        {labels.length > 0 && (
          <span className="active-work-flags">
            {labels.map((l) => (
              <span key={l} className="active-work-flag">
                {l}
              </span>
            ))}
            {queued > 0 && <span className="active-work-flag queued">Q{queued}</span>}
          </span>
        )}
      </button>
      <button
        type="button"
        className="active-work-close"
        title="Remove from Active strip (jobs keep running)"
        aria-label={`Close ${pinned.srKey}`}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        ×
      </button>
      {open && (
        <div className="active-work-popover" role="tooltip">
          <div className="muted small">
            {pinned.gameName || live?.gameName} · {pinned.texecKey || live?.texecKey}
          </div>
          <ul>
            {live?.jobs.map((j) => (
              <li key={j.id}>
                <code>{j.type}</code> — {j.status}
              </li>
            ))}
            {live?.flags.autoWorkflowBusy && (
              <li>
                Auto workflow — {live.autoWorkflowMessage || live.autoWorkflowStep || 'running'}
              </li>
            )}
            {live?.flags.texecAnalyzing && <li>TEXEC analyze-all — running</li>}
            {live?.flags.videoBusy && <li>Video — busy</li>}
            {live?.flags.imagesBusy && <li>Images — busy</li>}
            {!busy && <li>Pinned — idle right now</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function routeRunIdFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/runs\/([^/]+)/);
  return m?.[1] ?? null;
}

/**
 * Strip of runs the signed-in user has opened (pinned until X), with live
 * busy/idle icons from team activity. Click opens the run; X only unpins.
 */
export function ActiveWorkStrip() {
  const { user } = useAuth();
  const bindUser = useSessionRunsStore((s) => s.bindUser);
  const pinned = useSessionRunsStore((s) => s.pinned);
  const unpin = useSessionRunsStore((s) => s.unpin);
  const teamRuns = useActiveWorkStore((s) => s.runs);
  const fetchWork = useActiveWorkStore((s) => s.fetch);
  const navigate = useNavigate();
  const location = useLocation();
  const routeRunId = routeRunIdFromPath(location.pathname);

  useEffect(() => {
    bindUser(user?.email ?? null);
  }, [user?.email, bindUser]);

  useEffect(() => {
    if (!user) return;
    void fetchWork();
    const timer = setInterval(() => void fetchWork(), 15000);
    return () => clearInterval(timer);
  }, [user, fetchWork]);

  const liveById = useMemo(() => {
    const map = new Map<string, ActiveWorkRun>();
    for (const r of teamRuns) map.set(r.runId, r);
    return map;
  }, [teamRuns]);

  if (!user || pinned.length === 0) return null;

  return (
    <div className="active-work-strip" aria-label="My open runs">
      <span className="active-work-label muted small">Active</span>
      <div className="active-work-chips">
        {pinned.map((p) => (
          <ActiveWorkChip
            key={p.runId}
            pinned={p}
            live={liveById.get(p.runId) ?? null}
            active={routeRunId === p.runId}
            onOpen={() => navigate(`/runs/${p.runId}/phase/1`)}
            onClose={() => unpin(p.runId)}
          />
        ))}
      </div>
    </div>
  );
}
